import Box from "@mui/joy/Box";
import Button from "@mui/joy/Button";
import CircularProgress from "@mui/joy/CircularProgress";
import Sheet from "@mui/joy/Sheet";
import Table from "@mui/joy/Table";
import Typography from "@mui/joy/Typography";
import React from "react";
import { Task } from "../../../models/task";
import {
  AdminQuartzClientContext,
  TaskQuartzClientContext,
} from "../../../shared/services/api/contexts";
import { unixToDayjs } from "../../../shared/utils/time";
import { useSnackbar } from "../../../shared/components/layout/SnackbarContext";

interface ListTasksProps {
  readonly refreshKey?: number;
}

export default function ListTasks({ refreshKey }: ListTasksProps) {
  // Context
  const taskQuartzClient = React.useContext(TaskQuartzClientContext);
  const adminQuartzClient = React.useContext(AdminQuartzClientContext);
  const { showSnackbar } = useSnackbar();

  // State
  const [tasks, setTasks] = React.useState<Task[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [downloadingTaskId, setDownloadingTaskId] = React.useState<string | null>(null);

  // Effects
  React.useEffect(() => {
    let cancelled = false;

    const fetchTasks = async (showSpinner: boolean) => {
      if (showSpinner) {
        setLoading(true);
      }
      try {
        const fetchedTasks = await taskQuartzClient.listTasks();
        if (!cancelled) {
          const sorted = [...fetchedTasks].sort(
            (a, b) => Number(b.last_modified_date) - Number(a.last_modified_date)
          );
          setTasks(sorted);
        }
      } catch (error: any) {
        if (!cancelled && showSpinner) {
          showSnackbar({
            text: error?.message ?? "Unable to load tasks",
            color: "danger",
          });
        }
      } finally {
        if (!cancelled && showSpinner) {
          setLoading(false);
        }
      }
    };

    fetchTasks(true);
    const intervalId = setInterval(() => fetchTasks(false), 5000);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [taskQuartzClient, showSnackbar, refreshKey]);

  const handleDownload = async (task: Task) => {
    if (!task.output || task.output.type !== "resource") return;
    setDownloadingTaskId(task.task_id);
    try {
      await adminQuartzClient.downloadDataExport(task.task_id);
      showSnackbar({ text: "Export download started", color: "success" });
    } catch (error: any) {
      showSnackbar({
        text: error?.message ?? "Unable to download export",
        color: "danger",
      });
    } finally {
      setDownloadingTaskId(null);
    }
  };

  return (
    <Box sx={{ mt: 4 }}>
      <Typography level="body-lg" component="div" sx={{ pb: "5px" }}>
        Tasks
      </Typography>
      {loading && <CircularProgress size="sm" />}
      {tasks.length === 0 && !loading ? (
        <Typography>No task found</Typography>
      ) : (
        <Sheet sx={{ mt: 2, borderRadius: "8px", td: { verticalAlign: "middle" } }}>
          <Table
            aria-label="users table"
            hoverRow
            sx={{ borderRadius: "8px" }}
            variant="outlined"
            borderAxis="xBetween"
          >
            <thead>
              <tr>
                <th>Task ID</th>
                <th>Start date</th>
                <th>Last modified date</th>
                <th>Description</th>
                <th>Status</th>
                <th>Status reason</th>
                <th style={{ width: "140px" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {tasks.map((task: Task) => (
                <tr key={task.task_id}>
                  {/* Trunc task_id too long (max 10 chars) */}
                  <td>
                    {task.task_id.length > 10 ? `${task.task_id.slice(0, 10)}...` : task.task_id}
                  </td>
                  <td>{unixToDayjs(task.authored_on)!.format("YYYY MMM DD HH:mm:ss")}</td>
                  <td>{unixToDayjs(task.last_modified_date)!.format("YYYY MMM DD HH:mm:ss")}</td>
                  <td>{task.description}</td>
                  <td>{task.status}</td>
                  <td>{task.status_reason || "—"}</td>
                  <td>
                    {task.status === "completed" && task.output?.type === "resource" ? (
                      <Button
                        size="sm"
                        onClick={() => handleDownload(task)}
                        loading={downloadingTaskId === task.task_id}
                        disabled={downloadingTaskId === task.task_id}
                      >
                        Download
                      </Button>
                    ) : (
                      "—"
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Sheet>
      )}
    </Box>
  );
}
